"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

type Sesion = { id: string; nombre: string; rol: string };
type Estado = "borrador" | "activo" | "cerrado";
type Evento = { id: string; slug: string; nombre: string; fecha: string | null; estado: Estado; ventas: number; total_centavos: string | number; productos: number };

const ESTADO: Record<Estado, { label: string; color: string }> = {
  borrador: { label: "Borrador", color: "#737373" },
  activo: { label: "Activo", color: "#2FA96A" },
  cerrado: { label: "Cerrado", color: "#404040" },
};
const q = (c: string | number) => "Q" + (Number(c) / 100).toLocaleString("es-GT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const slugify = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
const DESCARGAS: [string, string][] = [["ventas", "Ventas"], ["productos", "Por producto"], ["bitacora", "Bitácora de inventario"]];

export default function PanelCliente({ operador }: { operador: Sesion }) {
  const router = useRouter();
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [cargando, setCargando] = useState(true);
  const [ok, setOk] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [descargas, setDescargas] = useState<string | null>(null);
  const esSuper = operador.rol === "superadmin";

  async function cargar() {
    const d = await fetch("/api/panel/eventos").then((r) => r.json()).catch(() => null);
    if (d?.ok) setEventos(d.eventos); else setError("No se pudo cargar la lista de eventos");
    setCargando(false);
  }
  useEffect(() => { cargar(); }, []);
  function flash(m: string) { setError(""); setOk(m); setTimeout(() => setOk(null), 3500); }

  async function entrar(ev: Evento) {
    const d = await fetch(`/api/panel/eventos/${ev.id}/entrar`, { method: "POST" }).then((r) => r.json()).catch(() => null);
    if (d?.ok) router.push(`/e/${ev.slug}/comandas`); else setError(d?.error ?? "No se pudo entrar al evento");
  }

  async function cambiarEstado(ev: Evento, estado: Estado, msg: string) {
    if (estado === "cerrado" && !window.confirm(
      `¿Cerrar "${ev.nombre}"?\n\nQueda en solo lectura: no se podrá vender, ajustar stock ni cambiar el branding. Los auto-pedidos sin cobrar se archivan. Reportes y descargas siguen disponibles.`
    )) return;
    const d = await fetch(`/api/panel/eventos/${ev.id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ estado }),
    }).then((r) => r.json()).catch(() => null);
    if (d?.ok) { flash(msg); cargar(); } else setError(d?.error ?? "No se pudo cambiar el estado");
  }

  async function salir() {
    await fetch("/api/auth/logout", { method: "POST" });
    router.refresh();
  }

  return (
    <main className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-4 py-3 border-b border-neutral-900">
        <div>
          <span className="font-black text-[var(--acento-2)]">Comandas</span>
          <span className="text-neutral-600 text-sm ml-2">Eventos · {operador.nombre}</span>
        </div>
        <button onClick={salir} className="text-sm text-neutral-500 hover:text-red-300">Salir</button>
      </header>

      {ok && <div className="bg-green-900/40 border-b border-green-700 text-green-300 text-sm text-center py-2">{ok}</div>}
      {error && <div className="bg-red-900/40 border-b border-red-700 text-red-300 text-sm text-center py-2">{error}</div>}

      <div className="flex-1 overflow-y-auto p-4 contenedor-app space-y-4">
        <NuevoEvento open={nuevoOpen} onToggle={() => setNuevoOpen((v) => !v)} eventos={eventos}
          onCreated={(m) => { flash(m); setNuevoOpen(false); cargar(); }} onError={setError} />

        {cargando && <p className="text-center text-neutral-600 text-sm py-8">Cargando…</p>}
        {!cargando && eventos.length === 0 && <p className="text-center text-neutral-600 text-sm py-8">Todavía no hay eventos.</p>}

        {eventos.map((ev) => (
          <div key={ev.id} className={`rounded-2xl bg-neutral-900 border border-neutral-800 p-4 ${ev.estado === "cerrado" ? "opacity-70" : ""}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  {ev.nombre}
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: ESTADO[ev.estado].color }}>{ESTADO[ev.estado].label}</span>
                </div>
                <div className="text-xs text-neutral-500 mt-1">/e/{ev.slug}{ev.fecha ? ` · ${ev.fecha}` : ""}</div>
              </div>
              <button onClick={() => entrar(ev)} className="shrink-0 px-4 py-2 rounded-xl bg-[var(--acento)] text-white text-sm font-bold">Entrar</button>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              {[["Ventas", String(ev.ventas)], ["Total", q(ev.total_centavos)], ["Productos", String(ev.productos)]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-neutral-950 py-2">
                  <div className="text-[11px] text-neutral-500">{l}</div>
                  <div className="font-bold text-sm">{v}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4 mt-4 text-xs">
              {ev.estado === "borrador" && (
                <button onClick={() => cambiarEstado(ev, "activo", `"${ev.nombre}" activado`)} className="text-green-400 hover:text-green-300 font-semibold">Activar</button>
              )}
              {ev.estado === "activo" && (
                <>
                  <button onClick={() => cambiarEstado(ev, "borrador", `"${ev.nombre}" pasó a borrador`)} className="text-neutral-400 hover:text-white">Pasar a borrador</button>
                  <button onClick={() => cambiarEstado(ev, "cerrado", `"${ev.nombre}" cerrado y archivado`)} className="text-red-300 hover:text-red-200 font-semibold">Cerrar evento</button>
                </>
              )}
              {ev.estado === "cerrado" && esSuper && (
                <button onClick={() => cambiarEstado(ev, "activo", `"${ev.nombre}" reabierto`)} className="text-neutral-400 hover:text-white">Reabrir</button>
              )}
              <button onClick={() => setDescargas(descargas === ev.id ? null : ev.id)} className="text-neutral-400 hover:text-white">Descargar ▾</button>
            </div>

            {ev.estado === "borrador" && ev.productos === 0 && (
              <ImportarDe ev={ev} eventos={eventos.filter((x) => x.id !== ev.id)} onDone={(m) => { flash(m); cargar(); }} onError={setError} />
            )}
            {descargas === ev.id && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-neutral-800">
                {DESCARGAS.map(([t, l]) => (
                  <a key={t} href={`/api/panel/eventos/${ev.id}/export?tipo=${t}`}
                    className="text-xs px-3 py-1.5 rounded-full border border-neutral-700 text-neutral-300 hover:border-[var(--acento-2)] hover:text-white">⬇ {l} (CSV)</a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </main>
  );
}

function NuevoEvento({ open, onToggle, eventos, onCreated, onError }: {
  open: boolean; onToggle: () => void; eventos: Evento[]; onCreated: (m: string) => void; onError: (e: string) => void;
}) {
  const [nombre, setNombre] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManual, setSlugManual] = useState(false);
  const [fecha, setFecha] = useState("");
  const [origen, setOrigen] = useState("");
  const [guardando, setGuardando] = useState(false);

  function cambiarNombre(v: string) { setNombre(v); if (!slugManual) setSlug(slugify(v)); }

  async function crear() {
    const s = slugify(slug);
    if (!nombre.trim() || !s) { onError("Nombre y slug requeridos"); return; }
    setGuardando(true);
    const d = await fetch("/api/panel/eventos", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nombre: nombre.trim(), slug: s, fecha: fecha || null, clonar_de: origen || null }),
    }).then((r) => r.json()).catch(() => ({ ok: false }));
    setGuardando(false);
    if (d.ok) {
      const n = nombre.trim();
      setNombre(""); setSlug(""); setSlugManual(false); setFecha(""); setOrigen("");
      onCreated(`"${n}" creado en borrador${origen ? " — clonado con stock en 0" : ""}. Entrá para prepararlo y luego activalo.`);
    } else onError(d.error ?? "No se pudo crear el evento");
  }

  if (!open) {
    return <button onClick={onToggle} className="w-full py-3 rounded-2xl border border-dashed border-neutral-700 text-neutral-400 hover:border-[var(--acento-2)] hover:text-white">+ Nuevo evento</button>;
  }
  return (
    <div className="rounded-2xl bg-neutral-900 border border-neutral-800 p-4 space-y-3">
      <div className="flex justify-between items-center"><h3 className="font-semibold">Nuevo evento</h3><button onClick={onToggle} className="text-neutral-500">✕</button></div>
      <input value={nombre} onChange={(e) => cambiarNombre(e.target.value)} placeholder="Nombre del evento (ej. Another World)"
        className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2" />
      <div className="flex items-center bg-neutral-950 border border-neutral-800 rounded-xl px-3">
        <span className="text-neutral-600 text-sm">/e/</span>
        <input value={slug} onChange={(e) => { setSlugManual(true); setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-")); }}
          placeholder="slug" className="flex-1 bg-transparent py-2 pl-1 outline-none" />
      </div>
      <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={{ colorScheme: "dark" }}
        className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2" />
      <select value={origen} onChange={(e) => setOrigen(e.target.value)} className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2">
        <option value="">En blanco</option>
        {eventos.map((ev) => <option key={ev.id} value={ev.id}>Clonar de: {ev.nombre}</option>)}
      </select>
      {origen && (
        <p className="text-xs text-neutral-500">
          Copia catálogo (fotos y precios), branding, modo y operadores asignados. El stock arranca en 0: cargalo con ajustes + para que quede en la bitácora.
        </p>
      )}
      <button onClick={crear} disabled={guardando} className="w-full py-3 rounded-xl bg-[var(--acento)] text-white font-bold disabled:opacity-40">
        {guardando ? "Creando…" : "Crear evento"}
      </button>
    </div>
  );
}

function ImportarDe({ ev, eventos, onDone, onError }: {
  ev: Evento; eventos: Evento[]; onDone: (m: string) => void; onError: (e: string) => void;
}) {
  const [origen, setOrigen] = useState("");
  const [guardando, setGuardando] = useState(false);
  if (eventos.length === 0) return null;

  async function importar() {
    if (!origen || guardando) return;
    setGuardando(true);
    const d = await fetch(`/api/panel/eventos/${ev.id}/importar`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ origen }),
    }).then((r) => r.json()).catch(() => ({ ok: false }));
    setGuardando(false);
    if (d.ok) onDone(`Catálogo importado en "${ev.nombre}": ${d.productos} productos con stock en 0`);
    else onError(d.error ?? "No se pudo importar");
  }

  return (
    <div className="flex gap-2 mt-3 pt-3 border-t border-neutral-800">
      <select value={origen} onChange={(e) => setOrigen(e.target.value)}
        className="flex-1 min-w-0 bg-neutral-950 border border-neutral-800 rounded-lg px-2 py-1.5 text-xs">
        <option value="">Sin catálogo — importar de…</option>
        {eventos.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
      </select>
      <button onClick={importar} disabled={!origen || guardando}
        className="px-3 py-1.5 rounded-lg bg-[var(--acento)] text-white text-xs font-bold disabled:opacity-40">
        {guardando ? "Importando…" : "Importar"}
      </button>
    </div>
  );
}
